"use client";

import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Save, Loader2, CheckCircle2, XCircle, MessageSquare } from "lucide-react";
import { Badge } from "../ui/badge";
import { DocumentManager } from "./DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { ReviewComment } from "../shared/ReviewComment";

interface DocumentationMaterialityData {
  richTextDocumentation: string;
}

interface DocumentationMaterialityProps {
  onDataChange: (data: DocumentationMaterialityData) => void;
  initialData: DocumentationMaterialityData | null;
  crossSectionAnswers?: Record<string, string | number | boolean>;
  companyName?: string;
}

export function DocumentationMateriality({
  onDataChange,
  initialData,
  crossSectionAnswers = {},
  companyName: propCompanyName,
}: DocumentationMaterialityProps) {
  const { isArchivalMode } = useArchivalMode();
  const [companyName, setCompanyName] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [reviewCommentOpen, setReviewCommentOpen] = useState(false);
  
  // Document states for M-14
  const [m14Documents, setM14Documents] = useState<any[]>([]);
  const [m14TextResponse, setM14TextResponse] = useState<any>(null);

  const [formData, setFormData] = useState({
    richTextDocumentation: "",
  });

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      if (propCompanyName) {
        setCompanyName(propCompanyName);
        return;
      }
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, [propCompanyName]);

  // Load M-14 data from SharePoint when component mounts
  useEffect(() => {
    const loadFromSharePoint = async () => {
      if (!companyName) return;

      try {
        const planningAPI = (window as any).planningAPI;
        if (!planningAPI?.loadResponses) {
          console.log("Planning API not available for Documentation Materiality");
          return;
        }

        const sharePointResponses = await planningAPI.loadResponses({
          companyName,
          sectionKey: "materiality"
        });

        if (sharePointResponses && sharePointResponses.success && sharePointResponses.data) {
          const responses = sharePointResponses.data.responses || sharePointResponses.data;

          // Load M-14 (Additional Documentation)
          if (responses['M-14']) {
            const m14Response = responses['M-14'];
            if (m14Response.textResponse) {
              setM14TextResponse(m14Response.textResponse);
              setFormData(prev => ({
                ...prev,
                richTextDocumentation: m14Response.textResponse.html || m14Response.textResponse.plainText || ''
              }));
            }
            if (m14Response.documents) {
              setM14Documents(m14Response.documents);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load Documentation Materiality responses from SharePoint:", error);
      }
    };

    loadFromSharePoint();
  }, [companyName]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        richTextDocumentation: initialData.richTextDocumentation || "",
      });
    }
  }, [initialData]);

  const handleSave = async () => {
    // Call the original onDataChange for local state management
    onDataChange(formData);
    
    // Save to SharePoint
    if (!companyName) {
      setSaveError("Company name not available");
      setSaveStatus('error');
      return;
    }

    setSaving(true);
    setSaveStatus('saving');
    setSaveError(null);

    try {
      const responses: Record<string, any> = {};

      // M-14: Additional Documentation (document type)
      const hasM14Documents = m14Documents && Array.isArray(m14Documents) && m14Documents.length > 0;
      const hasM14TextResponse = m14TextResponse && (
        (m14TextResponse.html && m14TextResponse.html.trim().length > 0) || 
        (m14TextResponse.plainText && m14TextResponse.plainText.trim().length > 0)
      );
      
      // Always save if we have ANY data (documents or textResponse)
      if (hasM14Documents || hasM14TextResponse) {
        // Build documents array (excluding textbox type)
        const fileDocuments = hasM14Documents 
          ? m14Documents.filter((doc: any) => doc.type !== 'textbox').map((doc: any) => ({
              name: doc.name,
              type: doc.type,
              url: doc.url,
              reference: doc.reference
            }))
          : [];
        
        // Build textResponse - only from state (from SharePoint)
        const m14TextResponseData = hasM14TextResponse ? m14TextResponse : null;
        
        responses['M-14'] = {
          questionId: 'M-14',
          selectedOption: null,
          textResponse: m14TextResponseData,
          documents: fileDocuments,
          lastUpdated: new Date().toISOString(),
          companyName
        };
      }

      // Save to SharePoint
      const planningAPI = (window as any).planningAPI;
      if (planningAPI?.saveAllResponses) {
        const saveResult = await planningAPI.saveAllResponses({
          companyName,
          sectionKey: "materiality",
          responses
        });

        if (saveResult && saveResult.success) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 3000);
        } else {
          throw new Error(saveResult?.error || "Failed to save to SharePoint");
        }
      } else {
        throw new Error("Planning API not available");
      }
    } catch (error: any) {
      console.error("Error saving Documentation Materiality:", error);
      setSaveError(error.message || "Failed to save documentation");
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 5000);
    } finally {
      setSaving(false);
    }
  };

  // Debug log for tracking cross-section answers (for future conditional logic)
  console.log(
    "DocumentationMateriality crossSectionAnswers:",
    crossSectionAnswers,
  );

  return (
    <div className="space-y-6">
      {/* Header with Review Comment Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">Documentation Materiality</h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setReviewCommentOpen(true)}
          className="text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
          title="Review Comment"
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Review Comment
        </Button>
      </div>
      
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Additional Documentation</h3>
        <DocumentManager
          sectionKey="materiality"
          groupKey="Documentation"
          itemId="M-14"
          itemQuestion="Document any additional information, notes, or explanations regarding materiality determination."
          itemTextResponse={m14TextResponse}
          itemDocuments={m14Documents}
          onDocumentsChange={setM14Documents}
          onTextResponseChange={setM14TextResponse}
        />
      </div>

      <div className="flex justify-end items-center gap-3 pt-6">
        {saveStatus === 'saved' && (
          <Badge variant="outline" className="border-green-600 text-green-600 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Saved successfully
          </Badge>
        )}
        {saveStatus === 'error' && (
          <Badge variant="outline" className="border-red-600 text-red-600 flex items-center gap-1">
            <XCircle className="h-3 w-3" />
            {saveError || "Error saving"}
          </Badge>
        )}
        <Button 
          onClick={handleSave} 
          className="flex items-center gap-2"
          disabled={isArchivalMode || saving || !companyName}
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Documentation
            </>
          )}
        </Button>
      </div>
      
      {/* Review Comment Modal */}
      <ReviewComment
        questionId="Materiality-Documentation"
        questionText="Documentation Materiality"
        isOpen={reviewCommentOpen}
        onClose={() => setReviewCommentOpen(false)}
        workspace="Materiality"
        subPhase="Documentation Materiality"
      />
    </div>
  );
}
